import {
  FormConsumer,
  RecursionField,
  useField,
  useFieldSchema,
  useForm,
} from "@formily/react";
import { IFormStep } from "@formily/antd-v5";
import { theme } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useStateMachine } from "little-state-machine";
import { FormButtonGroup, IRumbleFormScope } from ".";
import { scrollToElement } from "@rumble-it/common-lib/src/helpers/scrollTo";
import { Button } from "./Button";

interface IRumbleStepButtonsProps {
  position?: "top" | "bottom";
  showBack?: boolean;
  showNext?: boolean;
  showSubmit?: boolean;
  scrollTo?: boolean | string;
  align?: "left" | "right" | "center";
}

type ButtonState = {
  disabled: {
    [key: string]: boolean;
  };
};

export const RumbleStepButtonsRecursion = (props: IRumbleStepButtonsProps) => {
  const COMPONENT_NAME = RumbleStepButtonsRecursion.name;

  const { useToken } = theme;
  const { token } = useToken();
  const { state } = useStateMachine();
  const schema = useFieldSchema();

  const form = useForm();
  if (!form)
    throw new Error(
      `${COMPONENT_NAME}: Form missing, ensure to use this component within a FormProvider.`,
    );

  const field = useField();
  if (!field) throw new Error(`${COMPONENT_NAME}: Field missing (useField?)`);

  const scope = (state as { scope?: IRumbleFormScope }).scope;
  if (!scope)
    console.warn(
      `${COMPONENT_NAME}: 'scope' is missing. Ensure you are using this component within a Rumble form context.`,
    );

  const formStep = scope?.formStep as IFormStep;
  if (!formStep)
    throw new Error(`${COMPONENT_NAME}: 'formStep' is missing in 'scope'.`);

  const globalField = form.query("global").take();
  if (!globalField)
    throw new Error(
      `${COMPONENT_NAME}: field 'global' is missing. Ensure there is a field 'global' in the form schema.`,
    ); // Ensure global field exists.

  const DEFAULT_BUTTON_STATE: ButtonState = {
    disabled: {
      back: false,
      next: false,
      submit: false,
    },
  };
  const buttonState: ButtonState =
    globalField.data?.buttonState ?? DEFAULT_BUTTON_STATE;

  const position = props.position || "bottom";
  const showBack = props.showBack ?? true;
  const showNext = props.showNext ?? true;
  const showSubmit = props.showSubmit ?? true;
  const scrollTo = props.scrollTo ?? true;

  const componentStyle: React.CSSProperties = { height: token.controlHeight };
  if (position === "top") componentStyle.marginBottom = 20;
  else componentStyle.marginTop = 20;

  const handleBack = () => {
    if (buttonState.disabled.back) {
      console.warn(
        `${COMPONENT_NAME}: Back button is in disabled state, but back() was called. This is likely a bug. Skipping going to previous step.`,
      );
      return;
    }
    formStep.back();
    if (scrollTo !== false) {
      scrollToElement({
        element: scrollTo === true ? "#rumbledirect-form" : scrollTo,
      });
    }
  };

  const handleNext = () => {
    if (buttonState.disabled.next) {
      console.warn(
        `${COMPONENT_NAME}: Next button is in disabled state, but next() was called. This is likely a bug. Skipping going to next step.`,
      );
      return;
    }
    formStep.next();
    if (scrollTo !== false) {
      scrollToElement({
        element: scrollTo === true ? "#rumbledirect-form" : scrollTo,
      });
    }
  };

  const isButtonDisabled = (button: string) =>
    !!(buttonState?.disabled?.[button] || form.invalid);

  return (
    <div className="formily-form-step-buttons" style={componentStyle}>
      <FormConsumer>
        {() => (
          <FormButtonGroup align={props.align ?? "left"}>
            {showBack && formStep.allowBack && (
              <Button
                type="link"
                onClick={handleBack}
                icon={<ArrowLeftOutlined width="3em" color="red" />}
                className="font-semibold !text-black gap-2"
                style={{ minWidth: 80 }}
              >
                Vorige
              </Button>
            )}
            {showNext && formStep.allowNext && (
              <Button
                type="primary"
                onClick={handleNext}
                disabled={isButtonDisabled("next")}
                style={{ minWidth: 250 }}
              >
                Volgende
              </Button>
            )}
            {showSubmit && !formStep.allowNext && (
              <>
                {schema && schema.mapProperties
                  ? schema.mapProperties((schema, name) => {
                      return (
                        <RecursionField
                          key={name}
                          name={name}
                          schema={schema}
                        />
                      );
                    })
                  : null}
              </>
            )}
          </FormButtonGroup>
        )}
      </FormConsumer>
    </div>
  );
};

export default RumbleStepButtonsRecursion;
